import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import html2canvas from "html2canvas";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtLogCompact } from "@/lib/tz";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { sanitizeHtml } from "@/components/RichTextEditor";
import {
  MessageSquarePlus, Loader2, Camera, Upload, X, Bug, Lightbulb, Highlighter, Undo2, Check,
} from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "#bf3b4f";
const HIDE_ON = ["/login", "/reset", "/sign/"];

/** Draw red boxes on a captured screen to point at the problem. */
function Annotator({ src, onDone, onCancel }) {
  const imgRef = useRef(null);
  const wrapRef = useRef(null);
  const [boxes, setBoxes] = useState([]);
  const [draft, setDraft] = useState(null);

  const rel = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const p = e.touches?.[0] || e;
    return { x: (p.clientX - r.left) / r.width, y: (p.clientY - r.top) / r.height };
  };

  const finish = async () => {
    const img = imgRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    ctx.strokeStyle = "#ff2d4b";
    ctx.lineWidth = Math.max(3, canvas.width / 320);
    boxes.forEach((b) => {
      ctx.strokeRect(Math.min(b.x1, b.x2) * canvas.width, Math.min(b.y1, b.y2) * canvas.height,
        Math.abs(b.x2 - b.x1) * canvas.width, Math.abs(b.y2 - b.y1) * canvas.height);
    });
    onDone(canvas.toDataURL("image/png"));
  };

  const view = draft ? [...boxes, draft] : boxes;
  return (
    <div className="space-y-2" data-testid="fb-annotator">
      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
        <Highlighter className="h-3.5 w-3.5" /> Drag on the picture to highlight what's wrong.
      </div>
      <div ref={wrapRef} className="relative select-none touch-none rounded-md overflow-hidden border border-slate-200 cursor-crosshair"
        onMouseDown={(e) => { e.preventDefault(); const p = rel(e); setDraft({ x1: p.x, y1: p.y, x2: p.x, y2: p.y }); }}
        onMouseMove={(e) => { if (draft) { e.preventDefault(); setDraft({ ...draft, ...{ x2: rel(e).x, y2: rel(e).y } }); } }}
        onMouseUp={() => {
          if (draft) {
            const wide = Math.abs(draft.x2 - draft.x1) > 0.01;
            const tall = Math.abs(draft.y2 - draft.y1) > 0.01;
            if (wide && tall) setBoxes([...boxes, draft]);
            setDraft(null);
          }
        }}
        onMouseLeave={() => setDraft(null)}
        onTouchStart={(e) => { const p = rel(e); setDraft({ x1: p.x, y1: p.y, x2: p.x, y2: p.y }); }}
        onTouchMove={(e) => { if (draft) { e.preventDefault(); setDraft({ ...draft, ...{ x2: rel(e).x, y2: rel(e).y } }); } }}
        onTouchEnd={() => {
          if (draft) {
            const wide = Math.abs(draft.x2 - draft.x1) > 0.01;
            const tall = Math.abs(draft.y2 - draft.y1) > 0.01;
            if (wide && tall) setBoxes([...boxes, draft]);
            setDraft(null);
          }
        }}>
        <img ref={imgRef} src={src} alt="Screen capture" className="w-full block" draggable={false} />
        {view.map((b, i) => (
          <div key={i} className="absolute border-2 pointer-events-none" style={{
            borderColor: "#ff2d4b",
            left: `${Math.min(b.x1, b.x2) * 100}%`, top: `${Math.min(b.y1, b.y2) * 100}%`,
            width: `${Math.abs(b.x2 - b.x1) * 100}%`, height: `${Math.abs(b.y2 - b.y1) * 100}%`,
          }} />
        ))}
      </div>
      <div className="flex gap-1.5">
        <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="fb-annotate-undo"
          disabled={!boxes.length} onClick={() => setBoxes(boxes.slice(0, -1))}><Undo2 className="h-3 w-3 mr-1" />Undo</Button>
        <Button size="sm" className="h-7 text-[11px]" style={{ backgroundColor: CRIMSON }}
          onClick={finish} data-testid="fb-annotate-done"><Check className="h-3 w-3 mr-1" />Use this picture</Button>
        <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={onCancel}>Discard</Button>
      </div>
    </div>
  );
}

const dataUrlToBlob = (d) => {
  const [head, b64] = d.split(",");
  const mime = head.match(/:(.*?);/)[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

export function FeedbackWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("new");
  const [type, setType] = useState("Bug");
  const [what, setWhat] = useState("");
  const [expected, setExpected] = useState("");
  const [desc, setDesc] = useState("");
  const [shots, setShots] = useState([]);          // [{src, kind}]
  const [pending, setPending] = useState(null);    // capture awaiting annotation
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [mine, setMine] = useState(null);
  const fileRef = useRef(null);

  const hidden = !user || HIDE_ON.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (open && tab === "mine") api.myReports().then(setMine).catch(() => setMine([]));
  }, [open, tab]);

  const reset = () => {
    setType("Bug"); setWhat(""); setExpected(""); setDesc(""); setShots([]); setPending(null);
  };

  const capture = async () => {
    setBusy(true);
    setOpen(false);                                 // don't photograph our own dialog
    await new Promise((r) => setTimeout(r, 350));
    try {
      const canvas = await html2canvas(document.body, {
        logging: false, useCORS: true, scale: Math.min(1, 1400 / window.innerWidth),
        windowWidth: document.documentElement.clientWidth,
        height: window.innerHeight, y: window.scrollY,
      });
      setPending(canvas.toDataURL("image/png"));
    } catch {
      toast.error("Couldn't take a picture of this screen — you can attach your own instead.");
    } finally {
      setOpen(true); setBusy(false);
    }
  };

  const pickFiles = (e) => {
    const files = [...(e.target.files || [])].slice(0, 5);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setShots((s) => [...s, { src: reader.result, kind: "upload" }]);
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = async () => {
    setSending(true);
    try {
      const { issue_id } = await api.reportFeedback({
        type,
        description: type === "Feature Request" ? desc.trim() : "",
        what_happened: type === "Bug" ? what.trim() : "",
        expected_behaviour: type === "Bug" ? expected.trim() : "",
        context: {
          path: pathname, user_agent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`, app: "Prydes Support web",
        },
      });
      for (let i = 0; i < shots.length; i++) {
        const fd = new FormData();
        fd.append("file", dataUrlToBlob(shots[i].src), `screen-${i + 1}.png`);
        fd.append("kind", shots[i].kind);
        await api.uploadFeedbackAttachment(issue_id, fd);
      }
      api.triageFeedback(issue_id).catch(() => {});   // AI triage runs in the background
      toast.success(`Thanks — logged as ${issue_id}. We'll keep you posted.`);
      reset(); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send that just now. Please try again.");
    } finally { setSending(false); }
  };

  if (hidden) return null;
  const canSend = type === "Bug" ? what.trim().length > 2 : desc.trim().length > 2;
  // The worker app and portal have a bottom tab bar — sit above it, not on it.
  const hasTabBar = pathname.startsWith("/worker") || pathname.startsWith("/portal");

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-button"
        className={`fixed z-40 right-4 ${hasTabBar ? "bottom-20" : "bottom-4"} h-11 rounded-full px-4 shadow-lg text-white text-xs font-semibold
                   flex items-center gap-1.5 transition-transform hover:scale-105 active:scale-95`}
        style={{ backgroundColor: CRIMSON }} title="Report a problem or suggest an improvement">
        <MessageSquarePlus className="h-4 w-4" />Feedback
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPending(null); }}>
        <DialogContent className="max-h-[90vh] flex flex-col sm:max-w-xl" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Feedback</DialogTitle>
          </DialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
            <TabsList>
              <TabsTrigger value="new" data-testid="fb-tab-new">Report something</TabsTrigger>
              <TabsTrigger value="mine" data-testid="fb-tab-mine">My reports</TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="overflow-y-auto mt-3 space-y-3 pr-1">
              {pending ? (
                <Annotator src={pending}
                  onDone={(src) => { setShots([...shots, { src, kind: "capture" }]); setPending(null); }}
                  onCancel={() => setPending(null)} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {[["Bug", Bug, "Something's broken"], ["Feature Request", Lightbulb, "An idea to improve things"]].map(([v, Icon, sub]) => (
                      <button key={v} onClick={() => setType(v)} data-testid={`fb-type-${v === "Bug" ? "bug" : "feature"}`}
                        className={`rounded-lg border px-3 py-2 text-left transition-colors ${type === v ? "border-[#bf3b4f] bg-rose-50" : "border-slate-200 hover:bg-slate-50"}`}>
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-800">
                          <Icon className="h-3.5 w-3.5" style={{ color: type === v ? CRIMSON : undefined }} />{v}
                        </div>
                        <div className="text-[10px] text-slate-500">{sub}</div>
                      </button>
                    ))}
                  </div>

                  {type === "Bug" ? (
                    <>
                      <div>
                        <Label className="text-xs">What's happening?</Label>
                        <Textarea value={what} onChange={(e) => setWhat(e.target.value)} rows={3}
                          className="mt-1 text-sm" data-testid="fb-what"
                          placeholder="Tell us what went wrong and what you were doing at the time." />
                      </div>
                      <div>
                        <Label className="text-xs">What did you expect to happen?</Label>
                        <Textarea value={expected} onChange={(e) => setExpected(e.target.value)} rows={2}
                          className="mt-1 text-sm" data-testid="fb-expected"
                          placeholder="What should it have done instead?" />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label className="text-xs">What would you like the system to do?</Label>
                      <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4}
                        className="mt-1 text-sm" data-testid="fb-description"
                        placeholder="Describe the idea and how it would help you." />
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1.5">
                    <Button variant="outline" size="sm" className="h-8 text-[11px]" onClick={capture}
                      disabled={busy} data-testid="fb-capture">
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />}
                      Take a picture of this screen
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-[11px]"
                      onClick={() => fileRef.current?.click()} data-testid="fb-upload">
                      <Upload className="h-3.5 w-3.5 mr-1.5" />Attach from device
                    </Button>
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickFiles}
                      data-testid="fb-file-input" />
                  </div>

                  {!!shots.length && (
                    <div className="grid grid-cols-3 gap-2" data-testid="fb-shots">
                      {shots.map((s, i) => (
                        <div key={i} className="relative rounded-md overflow-hidden border border-slate-200">
                          <img src={s.src} alt={`Attachment ${i + 1}`} className="w-full h-20 object-cover" />
                          <button onClick={() => setShots(shots.filter((_, x) => x !== i))}
                            className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 shadow"
                            data-testid={`fb-shot-remove-${i}`}><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400">
                    We also record which screen you're on and your device type, so nobody has to ask.
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="mine" className="overflow-y-auto mt-3 space-y-2 pr-1">
              {mine === null && <div className="text-xs text-slate-400 py-6 text-center">Loading…</div>}
              {mine?.length === 0 && <div className="text-xs text-slate-400 py-6 text-center" data-testid="fb-mine-empty">You haven't reported anything yet.</div>}
              {(mine || []).map((r) => (
                <div key={r.issue_id} className="rounded-lg border border-slate-200 p-2.5" data-testid={`fb-mine-${r.issue_id}`}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-mono text-slate-400">{r.issue_id}</span>
                    <Badge className="text-[9px] py-0 bg-slate-100 text-slate-600 border border-slate-200">{r.type}</Badge>
                    <Badge className={`text-[9px] py-0 border ${r.status === "Resolved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>{r.status}</Badge>
                    <span className="text-[10px] text-slate-400">{fmtLogCompact(r.reported_at)}</span>
                  </div>
                  <div className="text-xs text-slate-800 mt-1">{r.title || r.what_happened || r.description}</div>
                  {r.updates?.map((u, i) => (
                    <div key={i} className="mt-1.5 rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-700">
                      <span className="text-slate-400">{u.by_name} · {fmtLogCompact(u.at)}</span>
                      <div>{u.text}</div>
                    </div>
                  ))}
                  {r.resolution && (
                    <div className="mt-1.5 rounded bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800">
                      <span className="font-medium">Resolved: </span>
                      <span
                        className="[&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_h2]:font-semibold [&_h3]:font-semibold [&_a]:underline [&_p]:mb-0.5 [&_table]:w-full [&_th]:border [&_th]:border-emerald-200 [&_th]:px-1 [&_td]:border [&_td]:border-emerald-200 [&_td]:px-1"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.resolution.note) }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>
          </Tabs>

          {tab === "new" && !pending && (
            <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={submit}
                disabled={sending || !canSend} data-testid="fb-submit">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Send feedback
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
